import React from "react";
import { PortWidget } from "@projectstorm/react-diagrams-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faPen } from "@fortawesome/free-solid-svg-icons";
import BoomIcon from "../../Common/BoomIcon";
import MyModal from "../../Common/MyModal";

const STYLE_BY_KIND = {
  solution: {
    border: "#8b5cf6",
    bg: "rgba(42, 18, 72, 0.96)",
  },
  step: {
    border: "#a855f7",
    bg: "rgba(47, 20, 79, 0.96)",
  },
  group: {
    border: "#22c55e",
    bg: "rgba(18, 60, 54, 0.94)",
  },
  input: {
    border: "#94a3b8",
    bg: "rgba(31, 24, 54, 0.95)",
  },
  component: {
    border: "#10b981",
    bg: "rgba(15, 45, 58, 0.95)",
  },
  componentRole: {
    border: "#f59e0b",
    bg: "rgba(67, 42, 14, 0.95)",
  },
  workflow: {
    border: "#a855f7",
    bg: "rgba(49, 25, 83, 0.96)",
  },
  event: {
    border: "#38bdf8",
    bg: "rgba(20, 46, 77, 0.95)",
  },
  condition: {
    border: "#ff2aa3",
    bg: "rgba(61, 15, 70, 0.96)",
  },
  decision: {
    border: "#ff2aa3",
    bg: "rgba(44, 14, 65, 0.98)",
  },
  actionGroup: {
    border: "#22d3ee",
    bg: "rgba(12, 48, 70, 0.96)",
  },
};

const WIDTH_BY_KIND = {
  solution: 260,
  step: 260,
  group: 240,
  input: 240,
  componentRole: 220,
  component: 260,
  workflow: 250,
  event: 240,
  condition: 320,
  decision: 320,
  actionGroup: 320,
};

function NodePort({ engine, port }) {
  const alignment = String(port.getOptions().alignment || "").toLowerCase();
  const isInPort = !!port.getOptions().in;
  const position =
    alignment === "left"
      ? { left: -7, top: "50%", transform: "translateY(-50%)" }
      : alignment === "right"
      ? { right: -7, top: "50%", transform: "translateY(-50%)" }
      : isInPort
      ? { top: -7, left: "50%", transform: "translateX(-50%)" }
      : { bottom: -7, left: "50%", transform: "translateX(-50%)" };
  return (
    <div
      style={{
        position: "absolute",
        width: 14,
        height: 14,
        borderRadius: 999,
        background: "#2b0a43",
        border: "2px solid #c084fc",
        boxShadow: "0 0 10px rgba(192, 132, 252, 0.75)",
        ...position,
      }}
    >
      <PortWidget engine={engine} port={port}>
        <div style={{ width: 14, height: 14 }} />
      </PortWidget>
    </div>
  );
}

export default function SolutionFlowNodeWidget({ node, engine, onNodeClick }) {
  const nodeKind = node.options.nodeKind || "step";
  const visual = STYLE_BY_KIND[nodeKind] || STYLE_BY_KIND.step;
  const borderColor = node.options.color || visual.border;
  const isFlowFocused = !!node.options.isFlowFocused;
  const focusedGlowColor = "rgba(236, 72, 153, 0.55)";
  const nodeWidth = WIDTH_BY_KIND[nodeKind] || 240;
  const ports = Object.values(node.getPorts() || {});
  const flowSelection = node?.options?.flowSelection;
  const details = Array.isArray(node.options.details)
    ? node.options.details
    : [];
  const variables = Array.isArray(node.options.variables)
    ? node.options.variables
    : [];
  const nodeIcon = node.options.icon;
  const isDecisionNode = nodeKind === "decision";
  const shouldModalizeDetails = [
    "condition",
    "decision",
    "actionGroup",
  ].includes(nodeKind);
  const inlineDetails = shouldModalizeDetails ? [] : details;
  const detailsModalHeading =
    nodeKind === "actionGroup" ? "Action details" : "Condition details";

  return (
    <div
      data-solution-flow-node="true"
      onClick={(event) => {
        if (!flowSelection) return;
        onNodeClick?.({
          ...flowSelection,
          clientX: event.clientX,
          clientY: event.clientY,
        });
      }}
      style={{
        position: "relative",
        width: nodeWidth,
        minHeight: isDecisionNode ? 180 : undefined,
        border: isDecisionNode ? "none" : `2px solid ${borderColor}`,
        background: isDecisionNode ? "transparent" : visual.bg,
        borderRadius: isDecisionNode ? 0 : nodeKind === "component" ? 28 : 8,
        padding: isDecisionNode ? "34px 44px" : "10px 12px",
        cursor: "zoom-in",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        textAlign: "center",
        boxShadow: isDecisionNode
          ? "none"
          : isFlowFocused
          ? `0 0 0 3px ${focusedGlowColor}, 0 0 18px ${focusedGlowColor}`
          : node.options.selected
          ? `0 0 0 3px ${borderColor}33`
          : `0 0 18px ${borderColor}33, inset 0 0 18px rgba(255, 255, 255, 0.04)`,
      }}
    >
      {isDecisionNode ? (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background: borderColor,
            clipPath: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
            filter: isFlowFocused
              ? `drop-shadow(0 0 16px ${focusedGlowColor})`
              : node.options.selected
              ? `drop-shadow(0 0 14px ${borderColor}99)`
              : `drop-shadow(0 0 14px ${borderColor}66)`,
            zIndex: 0,
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 2,
              background: visual.bg,
              clipPath: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
            }}
          />
        </div>
      ) : null}
      {nodeKind === "input" && flowSelection ? (
        <button
          type="button"
          className="btn btn-sm btn-light border"
          title="Edit input"
          aria-label="Edit input"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onNodeClick?.({
              ...flowSelection,
              action: "edit",
              clientX: event.clientX,
              clientY: event.clientY,
            });
          }}
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            width: 26,
            height: 26,
            padding: 0,
            zIndex: 2,
          }}
        >
          <FontAwesomeIcon icon={faPen} />
        </button>
      ) : null}
      {ports.map((port) => (
        <NodePort key={port.getID()} engine={engine} port={port} />
      ))}

      <div
        className="fw-bold"
        style={{
          position: "relative",
          zIndex: 1,
          color: "#f8f0ff",
          fontSize: 14,
          lineHeight: 1.2,
        }}
      >
        {nodeIcon ? (
          <div>
            <BoomIcon
              icon={nodeIcon}
              width={44}
              height={44}
              className="svg-white fw-light"
              style={{ flexShrink: 0 }}
              aria-hidden="true"
            />
          </div>
        ) : null}
        <span>{node.options.title}</span>
      </div>
      {node.options.subtitle ? (
        <div
          style={{
            position: "relative",
            zIndex: 1,
            color: "#c4b5fd",
            fontSize: 12,
          }}
        >
          {node.options.subtitle}
        </div>
      ) : null}
      {node.options.meta ? (
        <div
          style={{
            position: "relative",
            zIndex: 1,
            color: "#a78bfa",
            fontSize: 11,
          }}
        >
          {node.options.meta}
        </div>
      ) : null}
      {details.length && shouldModalizeDetails ? (
        <div className="mt-2" style={{ position: "relative", zIndex: 1 }}>
          <MyModal
            label={
              <span className="d-inline-flex align-items-center gap-1">
                <FontAwesomeIcon icon={faCircleInfo} />
                Details
              </span>
            }
            heading={detailsModalHeading}
            size="lg"
            centered
            buttonClassName="btn btn-sm btn-outline-light px-2 py-1"
          >
            <div className="d-flex flex-column gap-2">
              <div className="fw-bold">{node.options.title}</div>
              {details.map((detail, index) => (
                <div
                  key={`${detail}-${index}`}
                  className="border rounded p-2 bg-light"
                  style={{ overflowWrap: "anywhere" }}
                >
                  {detail}
                </div>
              ))}
            </div>
          </MyModal>
        </div>
      ) : null}
      {inlineDetails.length ? (
        <div
          className="mt-2 d-flex flex-column gap-1"
          style={{ position: "relative", zIndex: 1 }}
        >
          {inlineDetails.slice(0, 5).map((detail, index) => (
            <div
              key={`${detail}-${index}`}
              style={{
                color: "#e9d5ff",
                fontSize: 11,
                lineHeight: 1.25,
                overflowWrap: "anywhere",
              }}
            >
              {detail}
            </div>
          ))}
          {inlineDetails.length > 5 ? (
            <div style={{ color: "#a78bfa", fontSize: 11 }}>
              +{inlineDetails.length - 5} more
            </div>
          ) : null}
        </div>
      ) : null}
      {variables.length ? (
        <div
          className="mt-2 d-flex flex-wrap justify-content-center gap-1"
          style={{ position: "relative", zIndex: 1 }}
        >
          {variables.slice(0, 6).map((variable, index) =>
            (() => {
              const variableLabel =
                variable?.label ||
                (typeof variable === "string" ? variable : "Variable");

              return (
                <button
                  type="button"
                  key={`${variableLabel}-${index}`}
                  className="badge fw-normal"
                  title="Edit variable"
                  aria-label={`Edit ${variableLabel}`}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (!variable?.flowSelection) return;

                    onNodeClick?.({
                      ...variable.flowSelection,
                      action: "edit",
                      clientX: event.clientX,
                      clientY: event.clientY,
                    });
                  }}
                  style={{
                    background: "rgba(236, 72, 153, 0.14)",
                    border: "1px solid rgba(236, 72, 153, 0.45)",
                    color: "#f0abfc",
                    fontSize: 10,
                    maxWidth: "100%",
                    whiteSpace: "normal",
                    textAlign: "center",
                    cursor: variable?.flowSelection ? "pointer" : "default",
                  }}
                >
                  {variableLabel}
                </button>
              );
            })()
          )}
          {variables.length > 6 ? (
            <span
              className="badge fw-normal"
              style={{
                background: "rgba(168, 85, 247, 0.16)",
                border: "1px solid rgba(168, 85, 247, 0.45)",
                color: "#c4b5fd",
              }}
            >
              +{variables.length - 6}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
